import { assertNotEmpty } from '@festina-life/core'
import type { RawLazyTextInputProps } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useAvailableComponents } from '../../../hooks/useAvailableComponents'
import { useHook, useService } from '../../../lenteHooks'
import { useOptions } from './useOptions'

export const useLazyResolveFormValue = () => {
  const translate = useTranslate()
  const formatFullName = useService('formatter.personEntityNameFormatter')

  const { isUsingLegalEntity } = useAvailableComponents()
  const { isOption } = useOptions()

  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { mutateAsync: fetchExternalId } = useHook(
    'lazyFetchers.crm.useLazyFetchExternalId',
  )()
  const { mutateAsync: fetchPersonVersion } = useHook(
    'lazyFetchers.crm.useLazyFetchPersonVersionLatest',
  )()

  return useCallback<RawLazyTextInputProps['resolveFormValue']>(
    async ({ formValue, option }) => {
      if (!isOption(option)) {
        throw new Error(translate('validation.ssn'))
      }

      let userInput
      switch (option) {
        case 'external':
        case 'pseudoExternal':
          userInput = await fetchExternalId({ tenantId, internalId: formValue })
          break
        case 'internal':
          userInput = formValue
          break
      }
      assertNotEmpty(userInput, translate('validation.ssn'))

      const personVersion = isUsingLegalEntity
        ? await fetchPersonVersion({
            tenantId,
            personId: formValue,
          })
        : null

      return {
        userInput,
        display: personVersion
          ? formatFullName(personVersion.personEntityNameVersion)
          : '',
      }
    },
    [
      fetchExternalId,
      fetchPersonVersion,
      formatFullName,
      isOption,
      isUsingLegalEntity,
      translate,
      tenantId,
    ],
  )
}
